/**
 * Corporate landing · public.
 *   /:slug  →  hero + benefits preview + "Sign in with your work email" CTA
 *
 * Fetches `/api/corporate/portal/{slug}`. On 404, shows a soft not-found
 * that keeps LC parent branding intact (no need to render the marketing
 * homepage — this URL was almost certainly typed / linked deliberately).
 */
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { corporateApi } from "@/lib/corporate-api";
import CorporateShell from "./CorporateShell";

const T = {
  bg: "#F4EFE6", card: "#FCFAF4", ink: "#211C15", ink2: "#5C5447",
  ink3: "#8A8071", line: "#E5DCCB", line2: "#D8CDB7", brass: "#8B6914",
  briefWa: "#25D366",
};

const BENEFIT_LABEL = {
  table:    "Reserve any table",
  stay:     "Find any stay",
  fit:      "Move, restore, feel best",
  airport:  "Airport, seamless",
  care:     "Looked after",
  escape:   "Where would you escape?",
  enjoy:    "Name the night",
  maison:   "Maisons across the globe",
};

export default function CorporateLanding() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [tenant, setTenant] = useState(null);
  const [state, setState]   = useState("loading"); // loading | ready | not-found

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        // If a session already exists, jump straight to the dashboard.
        const me = await corporateApi.me().catch(() => null);
        if (alive && me?.tenant?.slug === slug?.toLowerCase()) {
          navigate(`/${slug}/dashboard`, { replace: true });
          return;
        }
        const t = await corporateApi.portal(slug);
        // v3.47 · Founder ruling 04 Jul 2026 — /demo (and any tenant
        // flagged `guest_mode`) skips OTP entirely. Auto-mint a guest
        // session and jump to the dashboard.
        if (alive && t?.guest_mode) {
          try {
            await corporateApi.guestSession(slug);
            navigate(`/${slug}/dashboard`, { replace: true });
            return;
          } catch (_) { /* fall through to normal landing */ }
        }
        if (alive) { setTenant(t); setState("ready"); }
      } catch (e) {
        if (alive) setState("not-found");
      }
    })();
    return () => { alive = false; };
  }, [slug, navigate]);

  if (state === "loading") {
    return (
      <div data-testid="corp-landing-loading"
           style={{ background: T.bg, minHeight: "100vh",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    color: T.ink3, fontFamily: "'Fraunces', Georgia, serif",
                    fontStyle: "italic" }}>
        Loading…
      </div>
    );
  }

  if (state === "not-found") {
    return (
      <CorporateShell tenant={null} member={null} showSignOut={false}>
        <div data-testid="corp-landing-not-found" style={{ padding: "80px 0", textAlign: "center" }}>
          <p style={{ letterSpacing: "0.14em", fontSize: 11, color: T.brass,
                      textTransform: "uppercase", margin: "0 0 12px" }}>
            Portal not found
          </p>
          <h1 style={{ fontFamily: "'Fraunces', Georgia, serif",
                       fontSize: "clamp(28px, 5vw, 44px)", fontWeight: 500,
                       color: T.ink, margin: "0 0 16px" }}>
            We couldn&apos;t find a corporate portal at&nbsp;
            <em style={{ color: T.brass }}>/{slug}</em>.
          </h1>
          <p style={{ color: T.ink2, fontSize: 15, maxWidth: 480, margin: "0 auto",
                      lineHeight: 1.55 }}>
            If your employer has enrolled Les Concierges as a benefit, please
            confirm the exact link with them.
          </p>
        </div>
      </CorporateShell>
    );
  }

  const benefits = (tenant.benefits_enabled || []).filter((b) => BENEFIT_LABEL[b]);

  return (
    <CorporateShell tenant={tenant} member={null} showSignOut={false}>
      {/* Hero */}
      <section data-testid="corp-landing-hero"
               style={{ padding: "48px 0 24px" }}>
        <p style={{ letterSpacing: "0.14em", fontSize: 11, color: T.brass,
                    textTransform: "uppercase", margin: "0 0 12px", fontWeight: 700 }}>
          A sponsored benefit · By {tenant.display_name}
        </p>
        <h1 style={{ fontFamily: "'Fraunces', Georgia, serif",
                     fontSize: "clamp(36px, 6vw, 56px)", fontWeight: 500,
                     lineHeight: 1.05, color: T.ink,
                     margin: "0 0 20px", maxWidth: 780 }}>
          Your Concierge® is on&nbsp;duty,{" "}
          <em style={{ color: T.brass }}>wherever&nbsp;you&nbsp;are.</em>
        </h1>
        <p style={{ color: T.ink2, fontSize: 17, lineHeight: 1.55,
                    maxWidth: 620, margin: "0 0 32px" }}>
          Reserve a table on any continent. Find a stay anywhere on earth.
          Move, restore, and be looked after &mdash; the impossible made quietly
          possible, sponsored by your employer.
        </p>

        <button
          data-testid="corp-landing-sign-in-cta"
          type="button"
          onClick={() => navigate(`/${slug}/sign-in`)}
          style={{
            background: T.ink, color: T.bg, border: 0, borderRadius: 999,
            padding: "14px 28px", fontSize: 14, fontWeight: 600,
            letterSpacing: "0.08em", textTransform: "uppercase",
            cursor: "pointer", fontFamily: "inherit",
            boxShadow: "0 8px 24px rgba(33,28,21,0.14)",
          }}
        >
          Sign in with your {tenant.display_name} email →
        </button>
        <p style={{ color: T.ink3, fontSize: 12, marginTop: 12 }}>
          Accepted:{" "}
          <span style={{ fontFamily: "ui-monospace, monospace", color: T.ink2 }}>
            {(tenant.email_domains || []).map((d) => `@${d}`).join(" · ")}
          </span>
        </p>
      </section>

      {/* Benefits preview */}
      {benefits.length > 0 && (
        <section data-testid="corp-landing-benefits"
                 style={{ marginTop: 24, borderTop: `1px solid ${T.line}`,
                          paddingTop: 32 }}>
          <p style={{ letterSpacing: "0.14em", fontSize: 11, color: T.brass,
                      textTransform: "uppercase", margin: "0 0 20px", fontWeight: 700 }}>
            What&apos;s included
          </p>
          <div style={{ display: "grid",
                        gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
                        gap: 12 }}>
            {benefits.map((b) => (
              <div
                key={b}
                data-testid={`corp-landing-benefit-${b}`}
                style={{
                  background: T.card, border: `1px solid ${T.line}`,
                  borderRadius: 12, padding: "16px 18px",
                }}
              >
                <div style={{ fontSize: 10.5, letterSpacing: "0.14em",
                              color: T.brass, textTransform: "uppercase",
                              fontWeight: 700, marginBottom: 6 }}>
                  /{b}
                </div>
                <div style={{ color: T.ink, fontSize: 14, fontWeight: 500 }}>
                  {BENEFIT_LABEL[b]}
                </div>
              </div>
            ))}
            {/* Upsell: Clubs (paid, routes to MindEscapes) */}
            <div
              data-testid="corp-landing-benefit-club-upsell"
              style={{
                background: `${T.brass}0f`, border: `1px dashed ${T.brass}`,
                borderRadius: 12, padding: "16px 18px",
              }}
            >
              <div style={{ fontSize: 10.5, letterSpacing: "0.14em",
                            color: T.brass, textTransform: "uppercase",
                            fontWeight: 700, marginBottom: 6 }}>
                /clubs · optional add-on
              </div>
              <div style={{ color: T.ink, fontSize: 14, fontWeight: 500,
                            marginBottom: 6 }}>
                A world of private clubs
              </div>
              <div style={{ color: T.ink3, fontSize: 12, lineHeight: 1.4 }}>
                Available separately via MindEscapes ↗
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Terms footer note */}
      <section style={{ marginTop: 40, paddingTop: 24,
                        borderTop: `1px solid ${T.line}`,
                        color: T.ink3, fontSize: 12, lineHeight: 1.55,
                        maxWidth: 720 }}>
        This benefit is provided at no cost to you and remains available
        while you are employed by {tenant.display_name}.{" "}
        {tenant.display_name} sees aggregate usage only &mdash; never the
        content of your individual requests. Fulfilment is handled by
        the Les Concierges team under our standard terms.
      </section>
    </CorporateShell>
  );
}
